import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { format, isPast } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { calendarService } from '../../services/calendarService';
import BookMeetingDialog from './BookMeetingDialog';

/**
 * MeetingDetailsDialog - Shows full meeting details with actions
 */
const MeetingDetailsDialog = ({ open, onClose, meeting, customerId, onMeetingUpdated }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'pl' ? pl : enUS;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);

  if (!meeting) return null;

  const isMeetingPast = isPast(meeting.end);

  const handleCall = () => {
    if (meeting.customerPhone) {
      window.location.href = `tel:${meeting.customerPhone}`;
    }
  };

  const handleEmail = () => {
    if (meeting.customerEmail) {
      window.location.href = `mailto:${meeting.customerEmail}`;
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('meeting.confirmCancel'))) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await calendarService.cancelMeeting(
        customerId,
        meeting.calendarName,
        meeting.id
      );

      if (onMeetingUpdated) {
        onMeetingUpdated();
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to cancel meeting');
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = () => {
    setShowReschedule(true);
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setShowReschedule(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('meeting.details')}</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Date and Time */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {t('meeting.date')}
                </Typography>
              </Box>
              <Typography variant="body1">
                {format(meeting.start, t('dateFormat.fullDate'), { locale: dateLocale })}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {t('meeting.time')}
                </Typography>
              </Box>
              <Typography variant="body1">
                {format(meeting.start, t('dateFormat.time'), { locale: dateLocale })} - {format(meeting.end, t('dateFormat.time'), { locale: dateLocale })}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Customer Information */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {t('meeting.customer')}
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 1 }}>
              {meeting.customerName}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {meeting.customerPhone && (
                <Button
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  onClick={handleCall}
                  size="small"
                >
                  {t('meeting.call')}
                </Button>
              )}
              
              {meeting.customerEmail && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={handleEmail}
                  size="small"
                >
                  {t('meeting.email')}
                </Button>
              )}
            </Box>

            {meeting.customerEmail && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {meeting.customerEmail}
              </Typography>
            )}
            
            {meeting.customerPhone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {meeting.customerPhone}
              </Typography>
            )}
          </Box>

          {/* Notes */}
          {meeting.notes && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {t('meeting.notes')}
                </Typography>
                <Typography variant="body2">
                  {meeting.notes}
                </Typography>
              </Box>
            </>
          )}

          {/* Calendar Name */}
          {meeting.calendarName && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('meeting.calendar')}
                </Typography>
                <Typography variant="body2">
                  {meeting.calendarName}
                </Typography>
              </Box>
            </>
          )}

          {/* Confirmation Status */}
          {meeting.confirmationStatus && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {t('confirmationStatus.label')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {meeting.confirmationStatus === 'confirmed' ? (
                    <>
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                      <Chip 
                        label={t('confirmationStatus.confirmed')} 
                        color="success" 
                        size="small" 
                        icon={<CheckCircleIcon />}
                      />
                    </>
                  ) : meeting.confirmationStatus === 'pending' ? (
                    <>
                      <HelpOutlineIcon sx={{ color: 'warning.main' }} />
                      <Chip 
                        label={t('confirmationStatus.pending')} 
                        color="warning" 
                        size="small" 
                        icon={<HelpOutlineIcon />}
                      />
                    </>
                  ) : meeting.confirmationStatus === 'declined' ? (
                    <Chip 
                      label={t('confirmationStatus.declined')} 
                      color="error" 
                      size="small" 
                    />
                  ) : (
                    <Chip 
                      label={meeting.confirmationStatus} 
                      size="small" 
                    />
                  )}
                </Box>
              </Box>
            </>
          )}

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
          color="error"
          disabled={loading || isMeetingPast}
        >
          {t('meeting.cancelMeeting')}
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        <Button
          onClick={handleReschedule}
          startIcon={<EditIcon />}
          variant="outlined"
          disabled={loading || isMeetingPast}
        >
          {t('meeting.reschedule')}
        </Button>
        
        <Button onClick={handleClose} variant="contained" disabled={loading}>
          {t('meeting.close')}
        </Button>
      </DialogActions>

      {/* Reschedule Dialog */}
      <BookMeetingDialog
        open={showReschedule}
        onClose={(success) => {
          setShowReschedule(false);
          if (success) {
            // Close the details dialog and refresh
            onClose();
            if (onMeetingUpdated) {
              onMeetingUpdated();
            }
          }
        }}
        customerId={customerId}
        calendarName={meeting.calendarName}
        existingMeeting={meeting}
        isRescheduling={true}
      />
    </Dialog>
  );
};

export default MeetingDetailsDialog;
